import Logger from "./logger";
import fastdom from "fastdom";

declare var dataLayer: any;

export default class Helpers {

	constructor() {

		this.detectTouch();
		this.detectKeyboard()
	}

	private detectTouch() {

		window.addEventListener('touchstart', (e) => { document.documentElement.classList.add('user-has-touched'); });

	}

	public static isTouch() {
		return document.documentElement.classList.contains('touch');
	}

	private detectKeyboard() {

		const htmlTag = document.documentElement;
		const usingKeyboardCSSClass = 'using-keyboard';

		document.addEventListener('keydown', () => {
			fastdom.mutate(() => {
				htmlTag.classList.add(usingKeyboardCSSClass);
			});
		});

		document.addEventListener('mousedown', () => {
			fastdom.mutate(() => {
				htmlTag.classList.remove(usingKeyboardCSSClass);
			});
		});

		document.addEventListener('touchstart', () => {
			fastdom.mutate(() => {
				htmlTag.classList.remove(usingKeyboardCSSClass);
			});
		});

	}

	public static isUsingKeyboard() {
		return document.documentElement.classList.contains('using-keyboard');
	}

	public static hasUserTouched() {

		return document.documentElement.classList.contains('user-has-touched');

	}

	public static createEventForDispatch(eventType: string) {

		let event;

		if (typeof (Event) === 'function') {

			event = new Event(eventType);

		}
		else {

			event = document.createEvent('Event');
			event.initEvent(eventType, true, true);

		}

		return event;

	}

	public static createCustomEventForDispatch(eventName: string, data: any = {}) {

		let event;
		if (window.CustomEvent && typeof window.CustomEvent === 'function') {
			event = new CustomEvent(eventName, { detail: data });
		} else {
			event = document.createEvent('CustomEvent');
			event.initCustomEvent(eventName, true, true, data);
		}

		return event;

	}

	public static getOffset(element: HTMLElement) {

		var rect = element.getBoundingClientRect(),
		scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
		scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		return { top: rect.top + scrollTop, left: rect.left + scrollLeft };

	}

	public static debounce(fn: Function, delay: number) {
		let timer: any = null;
		delay = delay || 300;

		const debouncedFunction = function () {
			const context = this;
			const args = arguments;
			clearTimeout(timer);
			timer = setTimeout(function () {
				fn.apply(context, args);
			}, delay);
		};

		debouncedFunction.cancel = function () {
			clearTimeout(timer);
		};

		return debouncedFunction;
	}

	public static getKeypressed(e: KeyboardEvent) {

		let key = e.key as String;

		if (key !== undefined) {

			return key;

		}

		let keyCode = e.keyCode as Number;

		if (keyCode !== undefined) {

			return keyCode;

		}

	}

	public static hash(string: String) {

		let hash = 0;

		if (string.length) {

			for (let i = 0; i < string.length; i++) {

				hash = (hash << 5) - hash + string.charCodeAt(i++) | 0;

			}

		}

		return hash.toString();

	}

	public static scopedLoop(list: any, fn: Function, context: any) {

		for (var i = 0, len = list.length; i < len; i++) {

			(function (i: any) {

				fn(list[i], i, context);

			}).call(this, i);

		}

	}

	public static serialize(form: HTMLElement, makeArray = false) {

		const serialized = [];

		const elements = form.querySelectorAll('input,select,textarea');

		for (var i = 0; i < elements.length; i++) {

			let field = elements[i] as any;

			if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'submit' || field.type === 'button') { continue; }

			if (field.type === 'select-multiple') {

				for (var j = 0; j < field.options.length; j++) {

					if (!field.options[j].selected) continue;
					if (makeArray) {
						serialized.push({
							name: encodeURIComponent(field.name),
							value: encodeURIComponent(field.options[j].value)
						});
					} else {
						serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.options[j].value));
					}
				}

			} else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
				if (makeArray) {
					serialized.push({
						name: encodeURIComponent(field.name),
						value: encodeURIComponent(field.value)
					});
				} else {
					serialized.push(encodeURIComponent(field.name) + "=" + encodeURIComponent(field.value));
				}
				

			}

		}

		if (makeArray) {
			return serialized;
		}

		return serialized.join('&');

	}

	public static isNumber(numberToTest: any) {
		const numberRegex = new RegExp('^[0-9]+$');
		return numberRegex.test(numberToTest);
	}

	public static scrollTo(to = 0, duration = 750) {

		const start = window.scrollY || window.pageYOffset;
		const change = to - start;
		const increment = 20;
		let currentTime = 0;

		const animateScroll = (() => {

			currentTime += increment;

			const val = Helpers.easeInOutQuad(currentTime, start, change, duration);

			window.scrollTo(0, val);

			if (currentTime < duration) {
				setTimeout(animateScroll, increment);
			}
		});

		animateScroll();
	};

	public static easeInOutQuad(t: number, b: number, c: number, d: number) {
		t /= d / 2;
		if (t < 1) return c / 2 * t * t + b;
		t--;
		return -c / 2 * (t * (t - 2) - 1) + b;
	}

	public static removeElement(element: Element) {
		if (element && element.parentNode) {
			element.parentNode.removeChild(element);
		}
	}

	public static loadHtml(url: string, parentElementId: string) {

		return fetch(url, {
			method: "GET",
			headers: { "Content-Type": "text/html" }
		})
			.then(function (response) {
				return response.text();
			})
			.then(function (body) {
				const parser = new DOMParser();
				const doc = parser.parseFromString(body, "text/html");
				return doc.getElementById(parentElementId);
			});
	};

	public static getParameter(name: string, locationSearch?: string): string {
		name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
		if (!locationSearch) {
			locationSearch = location.search;
		}
		const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
		const results = regex.exec(locationSearch);
		return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
	}

	public static updateQueryStringParameter(uri: string, key: string, value: string) {
		const i = uri.indexOf('#');
		const hash = i === -1 ? '' : uri.substr(i);
		uri = i === -1 ? uri : uri.substr(0, i);
		const re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
		const separator = uri.indexOf('?') !== -1 ? "&" : "?";
		if (uri.match(re)) {
			uri = uri.replace(re, '$1' + key + "=" + value + '$2');
		} else {
			uri = uri + separator + key + "=" + value;
		}
		return uri + hash;
	}

	public static guid() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
			return v.toString(16);
		});
	}

	public static preloadImage(src: string) {
		return new Promise<void>(function (resolve, reject) {
			if (document.querySelector('img[src="' + src + '"]')) {
				resolve();
			} else {
				var img = new Image()
				img.onload = ()=> resolve();
				img.onerror = () => reject();
				img.src = src;
				img.style.opacity = "0.0001";
				img.style.position = "fixed";
				img.style.pointerEvents = "none";
				img.style.top = "0px";
				img.style.left = navigator.userAgent.toLowerCase().indexOf('firefox') > -1 ? "0px" : "calc(100% - 1px)";
				img.style.zIndex = "100";
				document.body.appendChild(img);
				var preloadMeta = '<link rel="preload" href="' + src + '" as="image">';
				document.getElementsByTagName('head')[0].insertAdjacentHTML('beforeend', preloadMeta)
			}
		});
	}

	public static empty(element: HTMLElement) {
		while (element.firstChild) {
			element.removeChild(element.firstChild);
		}
	}

	public static fadeOut(element, callback?: Function) {
		element.style.opacity = 1;
		(function fade() {
			if ((element.style.opacity -= .1) < 0) {
				element.style.display = "none";
				callback();
			} else {
				requestAnimationFrame(fade);
			}
		})();
	}

	public static fadeIn(element, display = 'block', callback?: Function) {
		element.style.opacity = 0;
		element.style.display = display || "block";
		(function fade() {
			var val = parseFloat(element.style.opacity);
			if (!((val += .1) > 1)) {
				element.style.opacity = val;
				requestAnimationFrame(fade);
			} else {
				callback();
			}
		})();
	}

	public static pagination(totalItems: number, itemsPerPage: number, currentPageIndex: number) {

		const totalPages = Math.ceil(totalItems / itemsPerPage);
		const currentPage = Math.ceil(((currentPageIndex) / itemsPerPage) + 1);

		return {
			currentPage: currentPage,
			totalPages: totalPages
		};
	}

	public static truncateText(text: string, maxLength: number, tail = '...') {

		let textTruncated = text.trim();

		if (text.length > textTruncated.length) {
			let trimmedText = text.substr(0, maxLength);
			trimmedText = trimmedText.substr(0, Math.min(trimmedText.length, trimmedText.lastIndexOf(" ")));
			textTruncated = trimmedText + tail;
		}

		return textTruncated;
	}

	public static isHidden(element: HTMLElement) {

		if (!element) {
			return true;
		}
		return element.offsetParent === null;
	}

	// https://stackoverflow.com/questions/5916900/how-can-you-detect-the-version-of-a-browser
	public static getBrowser() {

		var ua = navigator.userAgent, tem, M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
		if (/trident/i.test(M[1])) {
			tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
			return { name: 'IE', version: (tem[1] || '') };
		}
		if (M[1] === 'Chrome') {
			tem = ua.match(/\bOPR|Edge\/(\d+)/)
			if (tem != null) { return { name: 'Opera', version: tem[1] }; }
		}
		M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
		if ((tem = ua.match(/version\/(\d+)/i)) != null) { M.splice(1, 1, tem[1]); }
		return {
			name: M[0],
			version: M[1]
		};

	}

	public static isIOS() {
		return (
			/iPad|iPhone|iPod/.test(navigator.userAgent) && 
			!(window as any).MSStream
		);
	}

	public static isWholeElementInViewport(el: HTMLElement) {
		let top = el.offsetTop;
		let left = el.offsetLeft;
		let width = el.offsetWidth;
		let height = el.offsetHeight;

		while (el.offsetParent) {
			//@ts-ignore
			el = el.offsetParent;
			top += el.offsetTop;
			left += el.offsetLeft;
		}

		return (
			top >= window.pageYOffset &&
			left >= window.pageXOffset &&
			(top + height) <= (window.pageYOffset + window.innerHeight) &&
			(left + width) <= (window.pageXOffset + window.innerWidth)
		);
	}

	public static clamp(val: number, min: number, max: number) {
		return val > max ? max : val < min ? min : val;
	}

	public static escapeHtml(unsafe) {
		return unsafe
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	}

	public static getPageTemplate() {
		const template = document.body.getAttribute('data-page-template');
		if (template) {
			return template;
		}
		return null;
	}

	public static async fetchWithTimeout(url: string, opts = {}, timeout = 5000) {

		const abortController = new AbortController();
		const { signal } = abortController;

		const _fetchPromise = fetch(url, {
			...opts,
			signal,
		});

		const timer = setTimeout(() => abortController.abort(), timeout);

		try {
			const result = await _fetchPromise;
			clearTimeout(timer);
			return result;
		} catch (error) {
			clearTimeout(timer);
			throw error;
		}

	}


	public static objectsAreTheSame(object1: any, object2: any) {
		try {
			return JSON.stringify(object1) === JSON.stringify(object2);
		} catch (error) {
			Logger.trackException(error);
			return false;
		}
	}

	public static deferTask(cb: Function, timeout = 250) {
		requestIdleCallback(() => {
			cb();
		}, { timeout: timeout });
	}

	public static getElementIndex(element) {
		if (!element || !element.parentNode) {
			return -1; // Return -1 if the element or its parent is not found
		}

		const children = Array.prototype.slice.call(element.parentNode.children);
		return children.indexOf(element);
	}

	public static roundToTwoDecimals(value: number) {
		return Math.round((value + Number.EPSILON) * 100) / 100;
	}

	public static searchDataLayer(searchFor: string): string {
		for (let i = 0; i < dataLayer.length; i++) {
			if (dataLayer[i][searchFor]) {
				return dataLayer[i][searchFor];
			}
		}
		return "";
	}

	public static activeSite() {
		return document.body.getAttribute("data-active-site-setting");
	}

}

(<any>window).Helpers = Helpers;

new Helpers();
